import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useQueryState, parseAsStringLiteral } from "nuqs";
import { apiRequest } from "@/lib/queryClient";
import { formatDateSafe } from "@/lib/utils";
import { Plan, Employee } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/hooks/usePermissions";
import { useProfileCompletion } from "@/hooks/useProfileCompletion";
import { useEffect } from "react";
import { CheckCircle2 } from "lucide-react";
import AgencyOrganizationTab from "./tabs/AgencyOrganizationTab";
import { BenefitsTab } from "./tabs/BenefitsTab";
import { ProfileTab } from "./tabs/ProfileTab";
import { EmployeesTab } from "./tabs/EmployeesTab";
import AgencyLoginManagement from "./tabs/AgencyLoginManagement";
import { RolesTab } from "./tabs/RolesTab";
import { TeamsTab } from "./tabs/TeamsTab";
import { DocumentsTab } from "./tabs/DocumentsTab";
import { CommissionManagementTab } from "./tabs/CommissionManagementTab";
import AgentCommissions from "@/pages/employee/AgentCommissions";
import TimeOffOverview from "@/pages/employer/timeoff/Overview";
import { EmployeesSectionToggle } from "@/components/employees/EmployeesSectionToggle";

// "login_management" is kept only so old `?tab=login_management` links still
// resolve — it now redirects to the Employees tab with the Logins sub-view.
const AGENCY_TABS = ["profile", "employees", "login_management", "roles", "teams", "commissions", "hr", "benefits", "documents", "organization"] as const;
const EMPLOYEE_VIEWS = ["employees", "logins"] as const;

// Map tabs to required permissions
const TAB_PERMISSIONS: Record<typeof AGENCY_TABS[number], string[]> = {
  profile: ["agency:view", "agency:edit"],
  employees: ["agency:members"],
  login_management: ["agency:members"],
  roles: ["agency:roles"],
  teams: ["agency:members"],
  commissions: [
    "agency_commissions:view",
    "agency_commissions:manage",
    "agency_commissions:reports",
    // Backward compatibility
    "commissions:manage",
    "commissions:reports",
    // Producers: opens the tab with the member-scoped "My Commissions" view
    // (the admin screen still requires the permissions above).
    "commissions:view",
  ],
  hr: ["agency_hr:view", "agency_hr:manage"],
  benefits: ["agency:benefits"],
  documents: ["documents:view"],
  organization: ["agency:view", "agency:organization"],
};

export default function Agency() {
  const [activeTab, setActiveTab] = useQueryState(
    "tab",
    parseAsStringLiteral(AGENCY_TABS).withDefault("profile")
  );
  const [employeesView, setEmployeesView] = useQueryState(
    "view",
    parseAsStringLiteral(EMPLOYEE_VIEWS).withDefault("employees")
  );

  // Legacy deep link: ?tab=login_management → Employees tab, Logins view
  useEffect(() => {
    if (activeTab === "login_management") {
      setActiveTab("employees");
      setEmployeesView("logins");
    }
  }, [activeTab]);

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const { can, canAny, isLoading: permissionsLoading } = usePermissions();
  const profileCompletion = useProfileCompletion();

  // Check which tabs the user can access
  const canAccessTab = (tab: typeof AGENCY_TABS[number]) => {
    const requiredPermissions = TAB_PERMISSIONS[tab];
    return canAny(requiredPermissions);
  };

  // Get list of accessible tabs (login_management is a sub-view of employees, not a tab)
  const accessibleTabs = AGENCY_TABS.filter(tab => tab !== "login_management" && canAccessTab(tab));

  // Redirect to first accessible tab if current tab is not accessible
  useEffect(() => {
    if (!permissionsLoading && activeTab && !canAccessTab(activeTab) && accessibleTabs.length > 0) {
      setActiveTab(accessibleTabs[0]);
    }
  }, [permissionsLoading, activeTab, accessibleTabs]);

  // Get agency/broker profile to use as "agencyId"
  const { data: profile } = useQuery({
    queryKey: ["/api/user/profile"],
    queryFn: async () => {
      const r = await fetch("/api/user/profile");
      if (!r.ok) throw new Error("Failed to load profile");
      return r.json();
    },
  });

  // Use agencyId from profile (works for both brokers and agency members)
  const agencyId = (profile?.agencyId || "").toString();

  // Fetch agency employees from API - filter to show only agency employees (not client employees)
  // Only fetch if user can access employees or benefits tab
  const canViewEmployees = canAccessTab("employees") || canAccessTab("benefits");
  const { data: employees = [] } = useQuery({
    queryKey: ["/api/employees", "agency"],
    queryFn: () => fetch("/api/employees").then(res => res.json()).then(data =>
      // Filter to show only employees not assigned to any client (agency employees)
      data.filter((emp: any) => !emp.clientId && !emp.prospectId)
    ),
    enabled: canViewEmployees,
  });

  // Only fetch if user can access benefits tab
  const canViewBenefits = canAccessTab("benefits");

  // The plans the agency offers its OWN employees (agency_plans links). Not
  // "every plan this agency created": plans.agencyId is set on every custom
  // plan built for any client, which turned this tab into a catch-all library.
  const { data: agencyPlans = [], isLoading: plansLoading } = useQuery<Plan[]>({
    queryKey: ["/api/agency-plans"],
    enabled: canViewBenefits,
  });

  const plansData: any[] = Array.isArray(agencyPlans)
    ? (agencyPlans as any[]).map((plan: any) => ({
        ...plan,
        clientPlanId: null, // No client-plan association at agency level
        status: plan.isActive === false ? "inactive" : "active",
      }))
    : [];

  // Fetch all plan contributions for calculating accurate premium splits
  const { data: allPlanContributions = [] } = useQuery({
    queryKey: ["/api/plan-contributions/all"],
    queryFn: () => fetch("/api/plan-contributions").then((res) => res.json()),
    enabled: canViewBenefits && !!plansData?.length,
  });

  // Fetch plan rates for accurate premium calculations
  const { data: planRates = [] } = useQuery({
    queryKey: ["/api/simple-plan-rates"],
    queryFn: () => fetch("/api/simple-plan-rates").then((res) => res.json()),
    enabled: canViewBenefits && !!plansData?.length,
  });

  // DEV-310: this used to overlay two localStorage caches onto the live
  // plan - neither `agency_*_customizations` nor `agency_*_rates` has any
  // writer anywhere in the codebase, so both branches were dead reads that
  // could only ever return stale data. Reduced to a passthrough; kept as a
  // function since it's passed as a prop into shared
  // BenefitsOverview/AvailablePlans components.
  const getEffectivePlan = (basePlan: Plan): Plan => basePlan;

  // Get actual plan rate for a coverage tier
  const getPlanRate = (planId: number, coverageTier: string) => {
    const rates = Array.isArray(planRates) ? planRates : [];

    // Map display names to database values
    const tierMap: { [key: string]: string } = {
      "Employee Only": "employee",
      "Employee + Spouse": "employeeSpouse",
      "Employee + Child(ren)": "employeeChild",
      "Employee + Children": "employeeChild",
      Family: "employeeFamily",
    };

    const dbTierName = tierMap[coverageTier] || coverageTier.toLowerCase();
    const rate = rates.find(
      (r: any) => r.planId === planId && r.rateStructure === dbTierName
    );
    return rate ? parseFloat(rate.premium) : 0;
  };

  // Calculate employer/employee contributions based on plan contribution settings
  const calculateContributions = (
    planId: number,
    coverageTier: string,
    totalPremium: number
  ) => {
    const contributions = Array.isArray(allPlanContributions)
      ? allPlanContributions
      : [];
    const planContributions = contributions.find(
      (contrib: any) => contrib.planId === planId
    );

    if (!planContributions) {
      // Fallback to basic calculation if no contribution settings found
      return {
        employerContribution: totalPremium * 0.8,
        employeeContribution: totalPremium * 0.2,
      };
    }

    if (
      planContributions.contributionStructure === "base_plan" &&
      planContributions.basePlanId
    ) {
      // Use base plan contributions if linked
      const basePlanContributions = contributions.find(
        (contrib: any) => contrib.planId === planContributions.basePlanId
      );
      if (basePlanContributions) {
        return calculateContributions(
          planContributions.basePlanId,
          coverageTier,
          totalPremium
        );
      }
    }

    let employerContribution = 0;
    let employeeContribution = totalPremium;

    // Determine which tier to use based on coverage
    let tierType = "employee";
    if (coverageTier.includes("Spouse") && coverageTier.includes("Child")) {
      tierType = "threeChildren"; // Family coverage
    } else if (coverageTier.includes("Spouse")) {
      tierType = "spouse";
    } else if (coverageTier.includes("Child")) {
      tierType = "oneChild"; // Simplified for demo
    }

    // Get contribution settings for the tier
    const contributionType =
      planContributions[`${tierType}ContributionType`] || "percentage";
    const contributionAmount = parseFloat(
      planContributions[`${tierType}Contribution`] || "0"
    );
    const maxAmount = parseFloat(
      planContributions[`${tierType}MaxAmount`] || "0"
    );

    if (contributionType === "percentage") {
      employerContribution = totalPremium * (contributionAmount / 100);
      if (maxAmount > 0) {
        employerContribution = Math.min(employerContribution, maxAmount);
      }
    } else if (contributionType === "fixed_amount") {
      employerContribution = Math.min(contributionAmount, totalPremium);
    }

    employeeContribution = totalPremium - employerContribution;

    return {
      employerContribution: Math.max(0, employerContribution),
      employeeContribution: Math.max(0, employeeContribution),
    };
  };

  // Removes the plan from the agency's own offering (the agency_plans link).
  // Never deletes the plan row: it may be an Administr library plan, or be
  // linked to clients.
  const deletePlanMutation = useMutation({
    mutationFn: async (agencyPlanId: number) =>
      apiRequest("DELETE", `/api/agency-plans/${agencyPlanId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/agency-plans"] });
      toast({
        title: "Success",
        description: "Plan removed from your agency's plans",
        variant: "success",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to remove plan",
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Agency Management</h1>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as typeof AGENCY_TABS[number])} className="space-y-6">
        <TabsList className={`grid w-full bg-[#eff6fe] dark:bg-gray-800`} style={{ gridTemplateColumns: `repeat(${accessibleTabs.length}, minmax(0, 1fr))` }}>
          {canAccessTab("profile") && (
            <TabsTrigger value="profile" data-tour="agency-tab-profile" className="flex items-center gap-1.5 bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">
              Profile
              {profileCompletion.isComplete && !profileCompletion.isLoading && (
                <CheckCircle2 className="h-3.5 w-3.5 text-green-500 data-[state=active]:text-green-300" />
              )}
            </TabsTrigger>
          )}
          {canAccessTab("employees") && (
            <TabsTrigger value="employees" data-tour="agency-tab-employees" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Employees</TabsTrigger>
          )}
          {canAccessTab("roles") && (
            <TabsTrigger value="roles" data-tour="agency-tab-roles" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Roles</TabsTrigger>
          )}
          {canAccessTab("teams") && (
            <TabsTrigger value="teams" data-tour="agency-tab-teams" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Teams</TabsTrigger>
          )}
          {canAccessTab("commissions") && (
            <TabsTrigger value="commissions" data-tour="agency-tab-commissions" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Commissions</TabsTrigger>
          )}
          {canAccessTab("hr") && (
            <TabsTrigger value="hr" data-tour="agency-tab-hr" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">HR</TabsTrigger>
          )}
          {canAccessTab("benefits") && (
            <TabsTrigger value="benefits" data-tour="agency-tab-benefits" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Benefits</TabsTrigger>
          )}
          {canAccessTab("documents") && (
            <TabsTrigger value="documents" data-tour="agency-tab-documents" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Documents</TabsTrigger>
          )}
          {canAccessTab("organization") && (
            <TabsTrigger value="organization" data-tour="agency-tab-organization" className="bg-[#eff6fe] dark:bg-gray-800 dark:text-gray-300 data-[state=active]:bg-[#1c35a7] data-[state=active]:text-white dark:data-[state=active]:bg-blue-600">Organization</TabsTrigger>
          )}
        </TabsList>

        {/* Profile Tab */}
        {canAccessTab("profile") && (
          <TabsContent value="profile" className="space-y-6">
            <ProfileTab />
          </TabsContent>
        )}

        {/* Employees Tab (hosts Login Management as a sub-view) */}
        {canAccessTab("employees") && (
          <TabsContent value="employees" className="space-y-4">
            <EmployeesSectionToggle value={employeesView} onChange={setEmployeesView} />
            {employeesView === "logins" ? <AgencyLoginManagement /> : <EmployeesTab />}
          </TabsContent>
        )}

        {/* Roles Tab */}
        {canAccessTab("roles") && (
          <TabsContent value="roles" className="space-y-6">
            <RolesTab />
          </TabsContent>
        )}

        {/* Teams Tab */}
        {canAccessTab("teams") && (
          <TabsContent value="teams" className="space-y-6">
            <TeamsTab />
          </TabsContent>
        )}

        {/* Commissions Tab — admins get the full management screen; members
            holding only commissions:view get their member-scoped My
            Commissions view (same component as /my-commissions), which
            already scopes data to the session user. */}
        {canAccessTab("commissions") && (
          <TabsContent value="commissions" className="space-y-6">
            {canAny([
              "agency_commissions:view",
              "agency_commissions:manage",
              "agency_commissions:reports",
              "commissions:manage",
              "commissions:reports",
            ]) ? (
              <CommissionManagementTab />
            ) : (
              <AgentCommissions />
            )}
          </TabsContent>
        )}

        {/* HR Tab — the agency's own time off (policies, requests, balances,
            holidays). Hosts the shared time-off overview; its links navigate to
            the full /time-off/* pages, which the broker router registers with
            the same agency scope. */}
        {canAccessTab("hr") && (
          <TabsContent value="hr" className="space-y-6">
            <TimeOffOverview />
          </TabsContent>
        )}

        {/* Benefits Tab */}
        {canAccessTab("benefits") && (
          <TabsContent value="benefits" className="space-y-6">
            <BenefitsTab
              plansData={plansData as any}
              employees={(employees as Employee[]) || []}
              agencyId={agencyId}
              plansLoading={plansLoading}
              getEffectivePlan={getEffectivePlan}
              formatDateSafe={formatDateSafe}
              getPlanRate={getPlanRate}
              calculateContributions={calculateContributions}
              deletePlanMutation={deletePlanMutation}
              setLocation={setLocation}
            />
          </TabsContent>
        )}

        {/* Documents Tab */}
        {canAccessTab("documents") && (
          <TabsContent value="documents" className="space-y-6">
            <DocumentsTab />
          </TabsContent>
        )}

        {/* Organization Tab */}
        {canAccessTab("organization") && (
          <TabsContent value="organization" className="space-y-6">
            <AgencyOrganizationTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
